import { useState, useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, useInView } from "framer-motion";
import Marquee from "react-fast-marquee";
import { Phone, WhatsappLogo, Wrench, Thermometer, Drop, Lightning, CheckCircle, Clock, ShieldCheck, CurrencyInr, MapPin, Star, Warning, CaretDown } from "@phosphor-icons/react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { captureGclid, getGclid, getSource } from "@/utils/gclid";
import RepairPage from "@/pages/RepairPage";
import MosquitoMeshPage from "@/pages/MosquitoMeshPage";
import WashingMachinePage from "@/pages/WashingMachinePage";
import FridgeRepairPage from "@/pages/FridgeRepairPage";
import AdminPage from "@/pages/AdminPage";
import SideBySidePage from "@/pages/SideBySidePage";
import ThankYouPage from "@/pages/ThankYouPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PHONE = "9742119056";
const WHATSAPP_URL = `https://wa.me/91${PHONE}?text=Hi%2C%20I%20need%20AC%20service%20in%20Bangalore`;

const SERVICES = [
  { name: "AC Inspection", price: "350", value: "inspection" },
  { name: "AC Jet Service", price: "650", value: "jet-service" },
  { name: "AC Cleaning", price: "849", value: "cleaning" },
  { name: "AC Foam Jet Service", price: "1200", value: "foam-jet" },
  { name: "AC Installation", price: "Call", value: "installation" },
  { name: "AC Removal", price: "800", value: "removal" },
  { name: "AC Gas Charging", price: "2800", value: "gas-charging" },
];

const PROBLEMS = [
  { icon: Thermometer, title: "AC Not Cooling?", desc: "Weak airflow or warm air? Could be gas leak, dirty filter, or compressor issue." },
  { icon: Drop, title: "Water Leaking?", desc: "Drain pipe block or frozen coil causing water drip? We fix it same day." },
  { icon: Warning, title: "Bad Smell?", desc: "Mould and bacteria buildup inside AC? Our deep cleaning removes it fully." },
  { icon: Lightning, title: "High Electricity Bill?", desc: "Dirty AC consumes 30% more power. Regular service saves you money." },
];

const TESTIMONIALS = [
  { name: "Rajesh K.", area: "Indiranagar", text: "Booked ₹350 inspection, technician came in 1 hour and fixed cooling issue. Very professional." },
  { name: "Priya M.", area: "Whitefield", text: "AC was leaking water badly. Called them, got fixed same day. No extra charges. Highly recommend." },
  { name: "Suresh R.", area: "Koramangala", text: "Best AC service in Bangalore. Honest pricing, quick response. My AC works like new now." },
  { name: "Anita S.", area: "HSR Layout", text: "Gas charging done perfectly. Technician explained everything. Very trustworthy service." },
  { name: "Mohammed A.", area: "Electronic City", text: "Foam jet cleaning was amazing. AC cooling improved 50%. Worth every rupee." },
];

const WHY_CHOOSE = [
  { icon: ShieldCheck, title: "No Hidden Charges", desc: "Price we quote is price you pay. Zero surprises." },
  { icon: MapPin, title: "Local Technicians", desc: "Bangalore-based experts who know your area." },
  { icon: Clock, title: "60-90 Min Response", desc: "Technician at your doorstep fast." },
  { icon: CurrencyInr, title: "Pay After Service", desc: "No advance payment. Pay only when satisfied." },
];

// --- Animated Section Wrapper ---
function AnimatedSection({ children, className = "", delay = 0 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

// --- Header ---
function Header() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/90 backdrop-blur-2xl border-b border-black/5 shadow-sm" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
        <span className="font-['Outfit'] font-black text-[#0A1128] text-lg tracking-tight uppercase">
          HOME TECH REPAIR
        </span>
        <a
          href={`tel:${PHONE}`}
          data-testid="header-call-btn"
          className="bg-[#FF3B30] text-white font-bold uppercase tracking-wider px-5 py-2.5 text-sm border-2 border-[#0A1128] shadow-[4px_4px_0px_0px_rgba(10,17,40,1)] hover:translate-y-0.5 hover:shadow-[2px_2px_0px_0px_rgba(10,17,40,1)] transition-all flex items-center gap-2"
        >
          <Phone size={18} weight="bold" />
          Call Now
        </a>
      </div>
    </header>
  );
}

// --- Hero Section ---
function HeroSection() {
  return (
    <section
      data-testid="hero-section"
      className="relative min-h-[90vh] flex items-center pt-16"
      style={{
        backgroundImage: `url(https://static.prod-images.emergentagent.com/jobs/741684ed-48ea-4edd-9177-1464ea48f837/images/3a4204f9a96803d65d91ac46212db35c955e671aeb323fb1f9255c9defbe2ffd.png)`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div className="hero-bg-overlay absolute inset-0" />
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left: Copy */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-block bg-[#FFC107] text-[#0A1128] text-sm font-bold uppercase tracking-[0.2em] px-4 py-1.5 border-2 border-[#0A1128] mb-6">
                Serving All Bangalore
              </span>
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-none uppercase text-[#0A1128] mb-6"
            >
              AC Not Cooling?
              <br />
              <span className="text-[#FF3B30]">We Fix It Today.</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-base sm:text-lg text-[#4B5563] leading-relaxed mb-4 max-w-lg"
            >
              Technician at your home in <strong className="text-[#0A1128]">60-90 minutes</strong>.
              AC Inspection only <strong className="text-[#FF3B30]">₹350</strong>. No hidden charges.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex items-center gap-3 text-sm text-[#4B5563] mb-8 flex-wrap"
            >
              <span className="flex items-center gap-1.5"><CheckCircle size={18} weight="fill" className="text-green-600" /> Same-day service</span>
              <span className="flex items-center gap-1.5"><CheckCircle size={18} weight="fill" className="text-green-600" /> No hidden charges</span>
              <span className="flex items-center gap-1.5"><CheckCircle size={18} weight="fill" className="text-green-600" /> Pay after service</span>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <a
                href={`tel:${PHONE}`}
                data-testid="hero-call-btn"
                className="bg-[#FF3B30] text-white hover:bg-[#D62828] font-bold uppercase tracking-wider px-8 py-4 border-2 border-[#0A1128] shadow-[6px_6px_0px_0px_rgba(10,17,40,1)] hover:translate-y-1 hover:shadow-[2px_2px_0px_0px_rgba(10,17,40,1)] transition-all flex items-center justify-center gap-2 text-lg"
              >
                <Phone size={22} weight="bold" />
                Call Now — {PHONE}
              </a>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-whatsapp-btn"
                className="bg-[#25D366] text-white hover:bg-[#1DA851] font-bold uppercase tracking-wider px-8 py-4 border-2 border-[#0A1128] shadow-[6px_6px_0px_0px_rgba(10,17,40,1)] hover:translate-y-1 hover:shadow-[2px_2px_0px_0px_rgba(10,17,40,1)] transition-all flex items-center justify-center gap-2 text-lg"
              >
                <WhatsappLogo size={22} weight="bold" />
                WhatsApp Us
              </a>
            </motion.div>
          </div>

          {/* Right: Quick Booking Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
          >
            <BookingForm />
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// --- Booking Form ---
function BookingForm() {
  const [form, setForm] = useState({ name: "", phone: "", area: "", service: "" });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.area || !form.service) {
      toast.error("Please fill all fields");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/leads`, { ...form, gclid: getGclid(), source: getSource() });
      if (res.data.success) {
        navigate(`/thank-you?name=${encodeURIComponent(form.name)}&service=ac-service`);
      }
    } catch {
      toast.error("Something went wrong. Please call us directly.");
    }
    setLoading(false);
  };

  return (
    <form
      onSubmit={handleSubmit}
      data-testid="booking-form"
      className="bg-white border-2 border-[#0A1128] p-6 sm:p-8 shadow-[8px_8px_0px_0px_rgba(10,17,40,1)]"
    >
      <h3 className="font-['Outfit'] text-xl sm:text-2xl font-bold text-[#0A1128] mb-1 uppercase tracking-tight">
        Book AC Service
      </h3>
      <p className="text-sm text-[#4B5563] mb-6">We will call you in 5-10 minutes</p>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-bold uppercase tracking-widest text-[#0A1128] mb-2">Name</label>
          <input
            data-testid="form-name"
            type="text"
            placeholder="Your name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="bg-white border-2 border-[#0A1128] p-3 text-base font-medium focus:ring-0 focus:border-[#FF3B30] focus:shadow-[4px_4px_0px_0px_rgba(255,59,48,1)] rounded-none w-full outline-none transition-all"
          />
        </div>
        <div>
          <label className="block text-sm font-bold uppercase tracking-widest text-[#0A1128] mb-2">Phone</label>
          <input
            data-testid="form-phone"
            type="tel"
            placeholder="Your phone number"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            className="bg-white border-2 border-[#0A1128] p-3 text-base font-medium focus:ring-0 focus:border-[#FF3B30] focus:shadow-[4px_4px_0px_0px_rgba(255,59,48,1)] rounded-none w-full outline-none transition-all"
          />
        </div>
        <div>
          <label className="block text-sm font-bold uppercase tracking-widest text-[#0A1128] mb-2">Area (Bangalore)</label>
          <input
            data-testid="form-area"
            type="text"
            placeholder="e.g. Koramangala, HSR Layout"
            value={form.area}
            onChange={(e) => setForm({ ...form, area: e.target.value })}
            className="bg-white border-2 border-[#0A1128] p-3 text-base font-medium focus:ring-0 focus:border-[#FF3B30] focus:shadow-[4px_4px_0px_0px_rgba(255,59,48,1)] rounded-none w-full outline-none transition-all"
          />
        </div>
        <div>
          <label className="block text-sm font-bold uppercase tracking-widest text-[#0A1128] mb-2">Service Required</label>
          <Select
            value={form.service}
            onValueChange={(val) => setForm({ ...form, service: val })}
          >
            <SelectTrigger
              data-testid="form-service-select"
              className="bg-white border-2 border-[#0A1128] p-3 h-auto text-base font-medium focus:ring-0 focus:border-[#FF3B30] rounded-none w-full"
            >
              <SelectValue placeholder="Select a service" />
            </SelectTrigger>
            <SelectContent>
              {SERVICES.map((s) => (
                <SelectItem key={s.value} value={s.value} data-testid={`service-option-${s.value}`}>
                  {s.name} {s.price !== "Call" ? `— ₹${s.price}` : "— Call for price"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <button
        type="submit"
        disabled={loading}
        data-testid="booking-submit"
        className="w-full mt-6 bg-[#FF3B30] text-white hover:bg-[#D62828] font-bold uppercase tracking-wider px-8 py-4 border-2 border-[#0A1128] shadow-[6px_6px_0px_0px_rgba(10,17,40,1)] hover:translate-y-1 hover:shadow-[2px_2px_0px_0px_rgba(10,17,40,1)] transition-all flex items-center justify-center gap-2 text-lg disabled:opacity-60"
      >
        {loading ? "Submitting..." : "Book Service Now"}
      </button>
    </form>
  );
}

// --- Problem Solution Section ---
function ProblemSection() {
  return (
    <section data-testid="problem-section" className="bg-[#F5F5F5] py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatedSection>
          <span className="text-sm font-bold tracking-[0.2em] uppercase text-[#FF3B30] mb-3 block">Common AC Problems</span>
          <h2 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight uppercase text-[#0A1128] mb-4">
            Facing These Issues?
          </h2>
          <p className="text-base sm:text-lg text-[#4B5563] mb-12 max-w-2xl">
            Our expert technicians diagnose and fix all AC problems <strong>same day</strong>. No guesswork. No repeat visits.
          </p>
        </AnimatedSection>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {PROBLEMS.map((p, i) => (
            <AnimatedSection key={i} delay={i * 0.1}>
              <div
                data-testid={`problem-card-${i}`}
                className="bg-white border-2 border-[#0A1128] p-6 shadow-[6px_6px_0px_0px_rgba(10,17,40,1)] hover:-translate-y-1 hover:shadow-[4px_4px_0px_0px_rgba(10,17,40,1)] transition-all h-full"
              >
                <p.icon size={40} weight="duotone" className="text-[#FF3B30] mb-4" />
                <h3 className="font-['Outfit'] text-lg font-bold text-[#0A1128] mb-2">{p.title}</h3>
                <p className="text-sm text-[#4B5563] leading-relaxed">{p.desc}</p>
              </div>
            </AnimatedSection>
          ))}
        </div>

        <AnimatedSection delay={0.4}>
          <div className="mt-12 text-center">
            <a
              href={`tel:${PHONE}`}
              data-testid="problem-section-call-btn"
              className="bg-[#FF3B30] text-white hover:bg-[#D62828] font-bold uppercase tracking-wider px-8 py-4 border-2 border-[#0A1128] shadow-[6px_6px_0px_0px_rgba(10,17,40,1)] hover:translate-y-1 hover:shadow-[2px_2px_0px_0px_rgba(10,17,40,1)] transition-all inline-flex items-center gap-2"
            >
              <Phone size={20} weight="bold" />
              Get Expert Help — Call {PHONE}
            </a>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
}

// --- Services & Pricing ---
function ServicesSection() {
  return (
    <section data-testid="services-section" className="bg-white py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatedSection>
          <span className="text-sm font-bold tracking-[0.2em] uppercase text-[#FF3B30] mb-3 block">Transparent Pricing</span>
          <h2 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight uppercase text-[#0A1128] mb-4">
            Services & Pricing
          </h2>
          <p className="text-base sm:text-lg text-[#4B5563] mb-12 max-w-2xl">
            No hidden fees. What you see is what you pay. Start with just <strong className="text-[#FF3B30]">₹350 inspection</strong>.
          </p>
        </AnimatedSection>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {SERVICES.map((s, i) => {
            const isHighlight = s.value === "inspection";
            return (
              <AnimatedSection key={s.value} delay={i * 0.08}>
                <div
                  data-testid={`service-card-${s.value}`}
                  className={
                    isHighlight
                      ? "bg-[#0A1128] text-white border-2 border-[#FF3B30] p-8 shadow-[8px_8px_0px_0px_rgba(255,59,48,1)] hover:-translate-y-2 transition-transform duration-300 h-full flex flex-col relative overflow-hidden"
                      : "bg-white border-2 border-[#0A1128] p-8 hover:-translate-y-2 transition-transform duration-300 shadow-[6px_6px_0px_0px_rgba(10,17,40,1)] flex flex-col h-full"
                  }
                >
                  {isHighlight && (
                    <span className="absolute top-0 right-0 bg-[#FF3B30] text-white text-xs font-bold uppercase tracking-widest px-3 py-1">
                      Most Popular
                    </span>
                  )}
                  <Wrench size={28} weight="duotone" className={isHighlight ? "text-[#FF3B30] mb-4" : "text-[#0A1128] mb-4"} />
                  <h3 className={`font-['Outfit'] text-lg font-bold mb-2 ${isHighlight ? "text-white" : "text-[#0A1128]"}`}>
                    {s.name}
                  </h3>
                  <div className="mt-auto pt-4">
                    {s.price !== "Call" ? (
                      <span className={`font-['Outfit'] text-3xl font-black ${isHighlight ? "text-[#FF3B30]" : "text-[#0A1128]"}`}>
                        ₹{s.price}
                      </span>
                    ) : (
                      <span className={`font-['Outfit'] text-xl font-bold ${isHighlight ? "text-[#FFC107]" : "text-[#FF3B30]"}`}>
                        Call for Price
                      </span>
                    )}
                  </div>
                </div>
              </AnimatedSection>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// --- Why Choose Us ---
function WhyChooseSection() {
  return (
    <section
      data-testid="why-choose-section"
      className="bg-[#0A1128] py-16 md:py-24 relative overflow-hidden"
      style={{
        backgroundImage: `url(https://static.prod-images.emergentagent.com/jobs/741684ed-48ea-4edd-9177-1464ea48f837/images/0279f4115a4cb5e3cf019fdb027bd1c428f6ca8672450c54d950bd14172d6c89.png)`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div className="absolute inset-0 bg-[#0A1128]/92" />
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatedSection>
          <span className="text-sm font-bold tracking-[0.2em] uppercase text-[#FFC107] mb-3 block">Why Bangalore Trusts Us</span>
          <h2 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight uppercase text-white mb-12">
            Why Choose Us?
          </h2>
        </AnimatedSection>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {WHY_CHOOSE.map((item, i) => (
            <AnimatedSection key={i} delay={i * 0.1}>
              <div
                data-testid={`why-card-${i}`}
                className="border-2 border-white/20 p-6 hover:border-[#FF3B30] transition-colors duration-300 h-full"
              >
                <item.icon size={36} weight="duotone" className="text-[#FFC107] mb-4" />
                <h3 className="font-['Outfit'] text-lg font-bold text-white mb-2">{item.title}</h3>
                <p className="text-sm text-white/70 leading-relaxed">{item.desc}</p>
              </div>
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>
  );
}

// --- Urgency Section ---
function UrgencySection() {
  return (
    <section data-testid="urgency-section" className="bg-[#FFC107] py-12 md:py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
          <AnimatedSection>
            <div className="p-4">
              <Clock size={36} weight="bold" className="text-[#0A1128] mx-auto mb-3" />
              <h3 className="font-['Outfit'] text-lg font-bold text-[#0A1128] uppercase">Slots Filling Fast Today</h3>
              <p className="text-sm text-[#0A1128]/80 mt-1">Limited technicians available. Book now.</p>
            </div>
          </AnimatedSection>
          <AnimatedSection delay={0.1}>
            <div className="p-4">
              <Warning size={36} weight="bold" className="text-[#0A1128] mx-auto mb-3" />
              <h3 className="font-['Outfit'] text-lg font-bold text-[#0A1128] uppercase">Avoid AC Breakdown</h3>
              <p className="text-sm text-[#0A1128]/80 mt-1">Peak summer can kill your compressor.</p>
            </div>
          </AnimatedSection>
          <AnimatedSection delay={0.2}>
            <div className="p-4">
              <CurrencyInr size={36} weight="bold" className="text-[#0A1128] mx-auto mb-3" />
              <h3 className="font-['Outfit'] text-lg font-bold text-[#0A1128] uppercase">Delay = Higher Cost</h3>
              <p className="text-sm text-[#0A1128]/80 mt-1">Small issue today, big repair tomorrow.</p>
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
}

// --- Testimonials Marquee ---
function TestimonialsSection() {
  return (
    <section data-testid="testimonials-section" className="bg-[#F5F5F5] py-16 md:py-24 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
        <AnimatedSection>
          <span className="text-sm font-bold tracking-[0.2em] uppercase text-[#FF3B30] mb-3 block">Real Reviews</span>
          <h2 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight uppercase text-[#0A1128]">
            What Customers Say
          </h2>
        </AnimatedSection>
      </div>

      {/* Kinetic marquee */}
      <div className="mb-10">
        <Marquee speed={40} gradient={false} className="py-2">
          {["SAVED MY SUMMER", "FAST REPAIR IN INDIRANAGAR", "HONEST PRICING", "BEST AC SERVICE", "HIGHLY RECOMMEND", "FIXED IN 1 HOUR"].map((t, i) => (
            <span
              key={i}
              className="font-['Outfit'] text-5xl sm:text-6xl lg:text-7xl font-black uppercase tracking-tighter text-transparent mx-6"
              style={{ WebkitTextStroke: "2px #0A1128" }}
            >
              {t}
              <span className="text-[#FF3B30] mx-4">&bull;</span>
            </span>
          ))}
        </Marquee>
      </div>

      {/* Review cards */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {TESTIMONIALS.slice(0, 3).map((t, i) => (
            <AnimatedSection key={i} delay={i * 0.1}>
              <div
                data-testid={`testimonial-card-${i}`}
                className="bg-white border-2 border-[#0A1128] p-6 shadow-[6px_6px_0px_0px_rgba(10,17,40,1)] h-full"
              >
                <div className="flex gap-1 mb-3">
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} size={18} weight="fill" className="text-[#FFC107]" />
                  ))}
                </div>
                <p className="text-[#4B5563] text-sm leading-relaxed mb-4">"{t.text}"</p>
                <div className="flex items-center gap-2 pt-3 border-t-2 border-[#E5E7EB]">
                  <div className="w-8 h-8 bg-[#0A1128] text-white flex items-center justify-center font-bold text-sm">
                    {t.name[0]}
                  </div>
                  <div>
                    <p className="text-sm font-bold text-[#0A1128]">{t.name}</p>
                    <p className="text-xs text-[#4B5563]">{t.area}</p>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>
  );
}

// --- Final CTA ---
function FinalCTA() {
  return (
    <section data-testid="final-cta-section" className="bg-[#FF3B30] py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <AnimatedSection>
          <h2 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight uppercase text-white mb-4">
            Stop Sweating.
            <br />
            Start Calling.
          </h2>
          <p className="text-white/90 text-base sm:text-lg mb-2">AC Inspection just ₹350. Technician in 60-90 mins.</p>
          <a
            href={`tel:${PHONE}`}
            data-testid="final-cta-phone"
            className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-black text-white tracking-tighter inline-block my-6 hover:scale-105 transition-transform"
          >
            {PHONE}
          </a>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`tel:${PHONE}`}
              data-testid="final-call-btn"
              className="bg-white text-[#FF3B30] hover:bg-[#F5F5F5] font-bold uppercase tracking-wider px-8 py-4 border-2 border-[#0A1128] shadow-[6px_6px_0px_0px_rgba(10,17,40,1)] hover:translate-y-1 hover:shadow-[2px_2px_0px_0px_rgba(10,17,40,1)] transition-all inline-flex items-center justify-center gap-2 text-lg"
            >
              <Phone size={22} weight="bold" />
              Call Now
            </a>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="final-whatsapp-btn"
              className="bg-[#25D366] text-white hover:bg-[#1DA851] font-bold uppercase tracking-wider px-8 py-4 border-2 border-[#0A1128] shadow-[6px_6px_0px_0px_rgba(10,17,40,1)] hover:translate-y-1 hover:shadow-[2px_2px_0px_0px_rgba(10,17,40,1)] transition-all inline-flex items-center justify-center gap-2 text-lg"
            >
              <WhatsappLogo size={22} weight="bold" />
              WhatsApp Us
            </a>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
}

// --- Footer ---
function Footer() {
  return (
    <footer data-testid="footer" className="bg-[#0A1128] py-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div>
            <span className="font-['Outfit'] font-black text-white text-lg tracking-tight uppercase block">HOME TECH REPAIR</span>
            <span className="text-white/60 text-sm">Bangalore's Trusted AC Service</span>
          </div>
          <div className="flex items-center gap-6 text-sm text-white/60">
            <a href={`tel:${PHONE}`} className="hover:text-white transition-colors flex items-center gap-1.5">
              <Phone size={16} weight="bold" /> {PHONE}
            </a>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors flex items-center gap-1.5">
              <WhatsappLogo size={16} weight="bold" /> WhatsApp
            </a>
          </div>
        </div>
        <div className="mt-6 pt-6 border-t border-white/10 text-center text-xs text-white/40">
          Home Tech Repair Bangalore. All rights reserved.
        </div>
      </div>
    </footer>
  );
}

// --- Sticky Mobile Call Button ---
function StickyCallButton() {
  return (
    <a
      href={`tel:${PHONE}`}
      data-testid="sticky-call-btn"
      className="sticky-call-btn md:hidden bg-[#FF3B30] text-white font-bold uppercase tracking-wider px-8 py-4 border-2 border-[#0A1128] shadow-[6px_6px_0px_0px_rgba(10,17,40,1)] flex items-center gap-2 text-lg"
    >
      <Phone size={22} weight="bold" />
      Call Now — {PHONE}
    </a>
  );
}

// --- Main App ---
function HomePage() {
  return (
    <div className="min-h-screen">
      <Header />
      <HeroSection />
      <ProblemSection />
      <ServicesSection />
      <WhyChooseSection />
      <UrgencySection />
      <TestimonialsSection />
      <FinalCTA />
      <Footer />
    </div>
  );
}

function App() {
  useEffect(() => { captureGclid(); }, []);
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/repair" element={<RepairPage />} />
        <Route path="/mosquito-mesh" element={<MosquitoMeshPage />} />
        <Route path="/washing-machine-repair" element={<WashingMachinePage />} />
        <Route path="/fridge-repair" element={<FridgeRepairPage />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/side-by-side-refrigerator" element={<SideBySidePage />} />
        <Route path="/thank-you" element={<ThankYouPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
